import { ArrowRight, Loader2, Sparkles, Square } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import PillToggle from "@/components/common/PillToggle";
import { useStore } from "@/lib/store";
import { cancelDebate } from "@/lib/debateApi";

/**
 * GoalInput — kicks off a new server-backed debate via POST /api/debates.
 * Parent owns the streaming lifecycle; we just hand off the new sessionId.
 */
export default function GoalInput({ onCreate }) {
  const onboardingGoal = useStore((s) => s.onboarding?.goal);
  const [goal, setGoal] = useState(onboardingGoal || "");
  const [mode, setMode] = useState("boardroom");
  const isStreaming = useStore((s) => s.isStreamingDebate);
  const currentSessionId = useStore((s) => s.currentDebateSessionId);
  const [submitting, setSubmitting] = useState(false);

  // Re-pick up onboarding goal if it changes (post-login hydration).
  useEffect(() => {
    if (onboardingGoal && !goal) setGoal(onboardingGoal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingGoal]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!goal.trim() || isStreaming || submitting) return;
    setSubmitting(true);
    try {
      await onCreate?.(goal.trim());
    } catch (err) {
      toast.error("Couldn't start debate", {
        description: err?.response?.data?.detail || err.message || "Try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const onCancel = async () => {
    if (!currentSessionId) return;
    try {
      await cancelDebate(currentSessionId);
      toast.message("Cancelling debate…");
    } catch (err) {
      toast.error("Cancel failed", {
        description: err?.response?.data?.detail || err.message,
      });
    }
  };

  return (
    <form
      data-testid="goal-input-form"
      onSubmit={submit}
      className="card-soft flex w-full items-center gap-3 rounded-full p-2 pl-5"
    >
      <Sparkles className="h-4 w-4 text-zinc-400" strokeWidth={2.4} />
      <input
        data-testid="goal-input"
        id="boardroom-goal-input"
        value={goal}
        onChange={(e) => setGoal(e.target.value)}
        placeholder="Set a goal for the boardroom… e.g. Launch MVP in 30 days"
        className="flex-1 bg-transparent py-2 text-[14px] text-zinc-900 placeholder:text-zinc-400 focus:outline-none"
        disabled={isStreaming}
      />
      <PillToggle
        testId="goal-mode-toggle"
        value={mode}
        onChange={setMode}
        size="sm"
        options={[
          { value: "boardroom", label: "Boardroom" },
          { value: "oneOnOne", label: "1:1" },
        ]}
      />
      {isStreaming ? (
        <button
          type="button"
          data-testid="goal-cancel"
          onClick={onCancel}
          className="group inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-[13px] font-semibold text-rose-700 transition-all hover:border-rose-300 hover:bg-rose-100"
        >
          <Square className="h-3 w-3 fill-current" strokeWidth={2.4} />
          Cancel debate
        </button>
      ) : (
        <button
          type="submit"
          data-testid="goal-submit"
          className="group inline-flex items-center gap-1.5 rounded-full bg-black px-4 py-2 text-[13px] font-semibold text-white transition-all hover:bg-zinc-800 disabled:opacity-50"
          disabled={!goal.trim() || submitting}
        >
          {submitting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <>
              Run debate
              <ArrowRight
                className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
                strokeWidth={2.4}
              />
            </>
          )}
        </button>
      )}
    </form>
  );
}
